import { Alert, Button, Card, Form, Input, Select, Space, Typography } from "antd";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useState, type ChangeEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { wharfService } from "@/api/services/wharfService";
import { areaLib } from "@/utils/areaLib";
import {
    HANDLING_ATTRIBUTE,
    PORT_BERTH_TYPE,
    PORT_WHARF_BERTH_GOODS_ENTERPRISE as ENTERPRISE,
    PORT_WHARF_BERTH_GOODS_TYPE as GOODS_TYPE,
    PORT_WHARF_BERTH_TYPE,
    WATERWAY
} from "@zhihaoscm/meta";
import { Dialog as ZhDialog, Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

export interface WharfManagementFormValues {
    type?: number;
    name: string;
    shortName: string;
    harborId: number | string;
    area?: string | null;
    contactName?: string | null;
    phone?: string | null;
    provinceCode: string;
    cityCode: string;
    regionCode: string;
    address: string;
    fullAddress?: string | null;
    latLon: string;
    displayArea?: string | string[] | null;
    portBerthType?: number | null;
    handlingAttribute: number | string;
    goodsType?: number | null;
    enterprise?: number | null;
    waterway?: number | null;
    remark?: string | null;
}

export interface WharfManagementFormPageProps {
    wharfId?: number | string;
    loadWharf?: (id: number) => Promise<PortWharfBerthVo>;
    createWharf?: (payload: WharfManagementFormValues) => Promise<unknown>;
    updateWharf?: (id: number, payload: WharfManagementFormValues) => Promise<unknown>;
}

/** 复用 Vue 码头详情接口。 */
export async function loadWharfManagementDetail(id: number): Promise<PortWharfBerthVo> {
    const response = await shippingRequestHttp.service.get<PortWharfBerthVo>(`/port/${id}`);
    return response.data;
}

/** 复用 Vue 码头新增接口。 */
export async function createWharfManagement(payload: WharfManagementFormValues) {
    const response = await shippingRequestHttp.service.post("/port", payload);
    return response.data;
}

/** 复用 Vue 码头编辑接口。 */
export async function updateWharfManagement(id: number, payload: WharfManagementFormValues) {
    const response = await shippingRequestHttp.service.put(`/port/${id}`, payload);
    return response.data;
}

const phonePattern = /^((1[3-9]\d{9})|((0\d{2,3}-?)?\d{7,8}(-\d{3,4})?))$/;
const landlinePrefixPattern = /^(010|02[0-9]|0[3-9][0-9]\d)$/;
const emptyToNull = (value: string | number | null | undefined) => (value === "" || value === undefined ? null : value);

/** 物流管理-码头新增/编辑，完整复用 Vue 字段、回显、校验和 POST/PUT payload。 */
export function WharfManagementFormPage({
    wharfId,
    loadWharf = loadWharfManagementDetail,
    createWharf = createWharfManagement,
    updateWharf = updateWharfManagement
}: WharfManagementFormPageProps) {
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const currentId = wharfId ?? params.id ?? searchParams.get("id") ?? undefined;
    const [form] = Form.useForm<WharfManagementFormValues>();
    const [pendingPayload, setPendingPayload] = useState<WharfManagementFormValues>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [harborLoading, setHarborLoading] = useState(false);
    const [harborOptions, setHarborOptions] = useState<Array<{ value: number; label: string }>>([]);
    const [errorMessage, setErrorMessage] = useState("");

    /** 复用旧 Vue remote-port-select 的分页接口，仅查询港口类型。 */
    const searchHarbors = async (keyword = "") => {
        setHarborLoading(true);
        try {
            const result = await wharfService.getPagingSelectorPort({
                page: 1,
                size: 10,
                portName: keyword || undefined,
                types: [PORT_WHARF_BERTH_TYPE.HARBOR.code]
            });
            setHarborOptions(
                result.list.map(port => ({
                    value: Number(port.id),
                    label: port.shortName || String(port.id)
                }))
            );
        } catch {
            setErrorMessage("所属港口加载失败");
        } finally {
            setHarborLoading(false);
        }
    };

    // 对齐 Vue onTelInput：输入固话区号后自动补齐连接符，方便继续录入号码。
    const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        form.setFieldValue("phone", landlinePrefixPattern.test(value) ? `${value}-` : value);
    };

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadWharf(Number(currentId))
            .then(detail => {
                if (disposed) return;
                form.setFieldsValue({
                    ...detail,
                    harborId: detail.harborId,
                    displayArea: detail.displayArea?.join("\n") || ""
                } as unknown as WharfManagementFormValues);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("码头详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, form, loadWharf]);

    const submitForm = (values: WharfManagementFormValues) => {
        const displayArea = Array.isArray(values.displayArea)
            ? values.displayArea
            : values.displayArea
                  ?.split(/\r?\n/)
                  .map(value => value.trim())
                  .filter(Boolean) || [];
        const payload: WharfManagementFormValues = {
            type: PORT_WHARF_BERTH_TYPE.PORT.code,
            name: values.name.trim(),
            shortName: values.shortName.trim(),
            harborId: Number(values.harborId),
            area: emptyToNull(values.area) as string | null,
            contactName: emptyToNull(values.contactName) as string | null,
            phone: emptyToNull(values.phone) as string | null,
            provinceCode: values.provinceCode,
            cityCode: values.cityCode,
            regionCode: values.regionCode,
            address: values.address.trim(),
            // Vue 确认保存时始终按最新省市区编码重建完整地址，避免编辑旧值残留。
            fullAddress:
                areaLib.getFullAreaName(values.provinceCode, values.cityCode, values.regionCode) +
                values.address.trim(),
            latLon: values.latLon,
            displayArea,
            portBerthType: values.portBerthType ?? null,
            handlingAttribute: Number(values.handlingAttribute),
            goodsType: values.goodsType ?? null,
            enterprise: values.enterprise ?? null,
            waterway: values.waterway ?? null,
            remark: emptyToNull(values.remark) as string | null
        };
        setPendingPayload(payload);
        setConfirmOpen(true);
    };

    const confirmSubmit = async () => {
        if (!pendingPayload) return;
        setLoading(true);
        try {
            if (currentId) await updateWharf(Number(currentId), pendingPayload);
            else await createWharf(pendingPayload);
            setConfirmOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage(currentId ? "码头修改失败" : "码头创建失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section aria-label="wharf-management-form-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>{currentId ? "修改码头" : "新增码头"}</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<WharfManagementFormValues>
                    form={form}
                    labelAlign="right"
                    labelCol={{ flex: "90px" }}
                    layout="horizontal"
                    initialValues={{ type: 1 }}
                    onFinish={values => void submitForm(values)}
                >
                    <Form.Item name="type" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="fullAddress" hidden>
                        <Input />
                    </Form.Item>
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <Form.Item label="名称" name="name" rules={[{ required: true, message: "请输入码头名称" }]}>
                            <Input aria-label="名称" allowClear maxLength={40} placeholder="请输入码头名称" showCount />
                        </Form.Item>
                        <Form.Item
                            label="简称"
                            name="shortName"
                            rules={[{ required: true, message: "请输入码头简称" }]}
                        >
                            <Input aria-label="简称" allowClear maxLength={20} placeholder="请输入码头简称" showCount />
                        </Form.Item>
                        <Form.Item
                            label="所属港口"
                            name="harborId"
                            rules={[{ required: true, message: "请选择所属港口" }]}
                        >
                            <Select
                                allowClear
                                filterOption={false}
                                loading={harborLoading}
                                options={harborOptions}
                                placeholder="请输入港口名称选择"
                                showSearch
                                onOpenChange={open => {
                                    if (open && harborOptions.length === 0) void searchHarbors();
                                }}
                                onSearch={keyword => void searchHarbors(keyword)}
                            />
                        </Form.Item>
                        <Form.Item
                            label="面积"
                            name="area"
                            rules={[{ pattern: /^$|^\+?[1-9]\d*$/, message: "请输入正整数" }]}
                        >
                            <Input
                                aria-label="面积"
                                allowClear
                                maxLength={16}
                                placeholder="请输入码头面积"
                                suffix="㎡"
                            />
                        </Form.Item>
                        <Form.Item
                            label="联系人"
                            name="contactName"
                            rules={[{ pattern: /^$|^[\u4e00-\u9fa5]+$/, message: "请输入中文姓名" }]}
                        >
                            <Input aria-label="联系人" allowClear maxLength={10} placeholder="请输入姓名" />
                        </Form.Item>
                        <Form.Item
                            label="手机号"
                            name="phone"
                            rules={[{ pattern: phonePattern, message: "请输入正确的手机号或固话" }]}
                        >
                            <Input
                                aria-label="手机号"
                                allowClear
                                placeholder="请输入电话或手机号"
                                onChange={handlePhoneChange}
                            />
                        </Form.Item>
                        <ZhGridItem span={1} />
                    </ZhGrid>
                    <Typography.Title level={5}>位置</Typography.Title>
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <Form.Item
                            label="省编码"
                            name="provinceCode"
                            rules={[{ required: true, message: "请选择省市区" }]}
                        >
                            <Input aria-label="省编码" placeholder="请选择省编码" />
                        </Form.Item>
                        <Form.Item label="市编码" name="cityCode" rules={[{ required: true, message: "请选择省市区" }]}>
                            <Input aria-label="市编码" placeholder="请选择市编码" />
                        </Form.Item>
                        <Form.Item
                            label="区编码"
                            name="regionCode"
                            rules={[{ required: true, message: "请选择省市区" }]}
                        >
                            <Input aria-label="区编码" placeholder="请选择区编码" />
                        </Form.Item>
                        <Form.Item
                            label="详细地址"
                            name="address"
                            rules={[{ required: true, message: "请输入详细地址" }]}
                        >
                            <Input aria-label="详细地址" maxLength={200} placeholder="请输入详细地址" />
                        </Form.Item>
                        <Form.Item
                            label="经纬度"
                            name="latLon"
                            rules={[{ required: true, message: "请选择位置地址后自动带出经纬度" }]}
                        >
                            <Input aria-label="经纬度" placeholder="请选择经纬度" />
                        </Form.Item>
                        <ZhGridItem span={1} />
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <Form.Item label="显示区域" name="displayArea">
                            <Input.TextArea aria-label="显示区域" placeholder="每行输入一个经纬度" />
                        </Form.Item>
                        <Form.Item label="码头类型" name="portBerthType">
                            <Select
                                aria-label="码头类型"
                                allowClear
                                options={PORT_BERTH_TYPE.options}
                                placeholder="请选择码头类型"
                            />
                        </Form.Item>
                        <Form.Item
                            label="装卸属性"
                            name="handlingAttribute"
                            rules={[{ required: true, message: "请选择装卸属性" }]}
                        >
                            <Select
                                aria-label="装卸属性"
                                allowClear
                                options={HANDLING_ATTRIBUTE.options}
                                placeholder="请选择装卸属性"
                            />
                        </Form.Item>
                        <Form.Item label="货物类型" name="goodsType">
                            <Select
                                aria-label="货物类型"
                                allowClear
                                options={GOODS_TYPE.options}
                                placeholder="请选择货物类型"
                            />
                        </Form.Item>
                        <Form.Item label="关联企业" name="enterprise">
                            <Select
                                aria-label="关联企业"
                                allowClear
                                options={ENTERPRISE.options}
                                placeholder="请选择关联企业"
                            />
                        </Form.Item>
                        <Form.Item label="所属航道" name="waterway">
                            <Select
                                aria-label="所属航道"
                                allowClear
                                options={WATERWAY.options}
                                placeholder="请选择所属航道"
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Form.Item label="简介" name="remark">
                        <Input.TextArea
                            aria-label="简介"
                            allowClear
                            maxLength={200}
                            placeholder="请输入码头简介"
                            rows={3}
                            showCount
                            style={{ resize: "none" }}
                        />
                    </Form.Item>
                    <Space className="operation-fixed-btn" size={0}>
                        <Button htmlType="submit" loading={loading} type="primary">
                            提交
                        </Button>
                        <Button className="transp-btn" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                title="提示"
                open={confirmOpen}
                modalRender={node => <div role="dialog">{node}</div>}
                onCancel={() => setConfirmOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmOpen(false)}>取消</Button>
                        <Button loading={loading} type="primary" onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                是否保存
            </ZhDialog>
        </section>
    );
}
